import type { ElementType, PropsWithChildren, ReactElement } from 'react';
import { mergeSx } from 'components/ui/utilities/mergeSx';
import type { TextLinkProps } from './TextLink.props';
import {
  StyledTextLink,
  textLinkAnchorSx,
  textLinkCompactSx,
  textLinkEmphasisSx,
  textLinkFooterSx,
  textLinkInlineAnchorSx,
  textLinkStatusActionSx,
} from './TextLink.styles';

const textLinkVisualVariantSx = {
  anchor: textLinkAnchorSx,
  inlineAnchor: textLinkInlineAnchorSx,
  emphasis: textLinkEmphasisSx,
  compact: textLinkCompactSx,
  footer: textLinkFooterSx,
  statusAction: textLinkStatusActionSx,
} as const;

const withDisabledSx = (disabled: boolean, sx: TextLinkProps['sx']) => {
  if (!disabled) {
    return sx;
  }

  const disabledStyles = { opacity: 0.6, pointerEvents: 'none' as const };
  return mergeSx(disabledStyles, sx);
};

const TextLink = <RootComponent extends ElementType = 'a',>({
  children,
  disabled = false,
  onClick,
  sx,
  tabIndex,
  underline = 'none',
  visualVariant,
  ...rest
}: PropsWithChildren<TextLinkProps<RootComponent>>): ReactElement => {
  const presetSx = visualVariant ? textLinkVisualVariantSx[visualVariant] : undefined;
  const variantSx = mergeSx(presetSx, sx);
  const mergedSx = withDisabledSx(disabled, variantSx);

  return (
    <StyledTextLink
      {...rest}
      aria-disabled={disabled || undefined}
      onClick={disabled ? undefined : onClick}
      sx={mergedSx}
      tabIndex={disabled ? -1 : tabIndex}
      underline={underline}
    >
      {children}
    </StyledTextLink>
  );
};

export default TextLink;
